import {
  createStyleMixin,
  draggableMixin,
  componentNameValidationMixin,
} from '@descope-ui/common/components-mixins';
import { createBaseClass } from '@descope-ui/common/base-classes';
import { compose } from '@descope-ui/common/utils';
import {
  forwardAttrs,
  getComponentName,
  injectStyle,
} from '@descope-ui/common/components-helpers';
import chevronIcon from './chevron.svg';
import { TextClass } from '@descope-ui/descope-text/class';

export const componentName = getComponentName('collapsible-container');

class RawCollapsibleContainer extends createBaseClass({
  componentName,
  baseSelector: 'slot',
}) {
  static get observedAttributes() {
    return ['collapsed', 'collapsible', 'text'];
  }

  constructor() {
    super();

    this.attachShadow({ mode: 'open' }).innerHTML = `
    <div class="wrapper">
      <descope-text st-host-direction="ltr">
        <div class="header">
          <div class="icon">
            <descope-icon st-fill="currentcolor" src=${chevronIcon}></descope-icon>
          </div>
          <span></span>
        </div>
      </descope-text>
      <div class="content">
        <slot></slot>
      </div>
    </div>
	`;

    injectStyle(
      `
      div.content {
        transition-property: max-height;
        overflow: auto;
        height: auto;
			}
			slot {
				box-sizing: border-box;
				width: 100%;
				display: flex;
			}
      slot:focus-visible {
        outline: none;
      }
			:host {
				display: inline-flex;
			}
      .header {
          user-select: none;
          display: flex;
          align-items: baseline;
      }
      .icon {
          flex-shrink: 0;
          height: var(${TextClass.cssVarList.textLineHeight});
          min-height: 24px;
          display: flex;
          align-items: center;
      }
      descope-icon {
          transition-property: transform;
      }
      .rotate {
          transform: rotate(-180deg);
      }
      descope-text {
        width: 100%;
        display: flex;
      }
      span {
        align-self: center;
        word-break: break-word;
      }
      span:empty {
        display: none;
      }
      .wrapper {
        overflow: hidden;
        display: inline-flex;
        width: 100%;
        flex-direction: column;
      }
  `,
      this,
    );

    this.header = this.shadowRoot.querySelector('.header');
    this.content = this.shadowRoot.querySelector('div.content');
    this.icon = this.header.querySelector('descope-icon');
    this.headerText = this.header.querySelector('span');
    this.textComponent = this.shadowRoot.querySelector('descope-text');
  }

  get isCollapsible() {
    return this.getAttribute('collapsible') === 'true';
  }

  calcContentHeight() {
    return this.content.offsetHeight || this.content.scrollHeight;
  }

  get isCollapsed() {
    return this.getAttribute('collapsed') === 'true';
  }

  toggle() {
    this.setAttribute('collapsed', this.isCollapsed ? 'false' : 'true');
  }

  collapse(animated = false) {
    const final = () => {
      this.content.style.maxHeight = '0px';
    };
    this.icon.classList.add('rotate');
    if (!animated) return final();

    this.content.style.maxHeight = this.calcContentHeight() + 'px';
    setTimeout(final);
  }

  expand(animated = false) {
    const final = () => {
      this.content.style.maxHeight = '';
    };
    this.icon.classList.remove('rotate');
    if (!animated) return final();

    this.content.addEventListener('transitionend', final, { once: true });
    this.content.style.maxHeight = this.calcContentHeight() + 'px';
  }

  #isComponentMounted = false;

  init() {
    super.init?.();
    // we want to animate the collapse/expand only after the first time the component is connected
    // so when setting a default value for the collapsed attribute, it will not animate
    this.#isComponentMounted = true;

    this.header.addEventListener('click', () => {
      if (!this.isCollapsible) return;
      this.toggle();
    });
    forwardAttrs(this, this.textComponent, {
      includeAttrs: ['text-align', 'text-variant', 'text-mode'],
      mapAttrs: { 'text-mode': 'mode', 'text-variant': 'variant' },
    });
  }

  updateHeaderText() {
    const text = this.getAttribute('text') || '';
    this.headerText.innerText = text;
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'text') {
      this.updateHeaderText();
      return;
    }
    const collapsed = this.isCollapsed && this.isCollapsible;
    if (collapsed) {
      this.collapse(this.#isComponentMounted);
    } else {
      this.expand(this.#isComponentMounted);
    }
  }
}

const selectors = {
  host: () => ':host',
  icon: () => 'descope-icon',
  iconWrapper: () => '.icon',
  header: () => '.header',
  text: () => 'span',
  wrapper: () => '.wrapper',
  content: () => 'div.content',
};

export const CollapsibleContainerClass = compose(
  createStyleMixin({
    mappings: {
      hostWidth: { selector: selectors.host, property: 'width' },
      hostDirection: { selector: selectors.host, property: 'direction' },
      verticalPadding: [
        { selector: selectors.wrapper, property: 'padding-top' },
        { selector: selectors.wrapper, property: 'padding-bottom' },
      ],
      horizontalPadding: [
        { selector: selectors.wrapper, property: 'padding-left' },
        { selector: selectors.wrapper, property: 'padding-right' },
      ],

      flexDirection: {},
      justifyContent: {},
      alignItems: {},
      gap: [{}, { property: 'margin-top' }],

      backgroundColor: { selector: selectors.wrapper },
      backgroundImage: { selector: selectors.wrapper },
      backgroundPositionX: { selector: selectors.wrapper },
      backgroundPositionY: { selector: selectors.wrapper },
      backgroundSize: { selector: selectors.wrapper },
      backgroundRepeat: { selector: selectors.wrapper },

      borderRadius: { selector: selectors.wrapper },
      borderColor: { selector: selectors.wrapper },
      borderStyle: { selector: selectors.wrapper },
      borderWidth: { selector: selectors.wrapper },

      boxShadow: { selector: selectors.wrapper },

      headerIconOrder: { selector: selectors.iconWrapper, property: 'order' },
      headerCursor: { selector: selectors.header, property: 'cursor' },
      headerGap: { selector: selectors.header, property: 'gap' },
      textGrow: { selector: selectors.text, property: 'flex-grow' },
      textDirection: { selector: selectors.text, property: 'direction' },
      iconAnimationDuration: {
        selector: selectors.icon,
        property: 'transition-duration',
      },
      contentAnimationDuration: {
        selector: selectors.content,
        property: 'transition-duration',
      },
    },
  }),
  draggableMixin,
  componentNameValidationMixin,
)(RawCollapsibleContainer);
